<template>
  <div id="app">
    <h1>AICAM - Object Detection Stream</h1>
    <div class="main-content">
      <div class="video-column">
        <div class="stream-container">
          <video id="video-stream" ref="videoRef" playsinline autoplay muted @playing="adjustCanvas"></video>
          <canvas id="overlay-canvas" ref="canvasRef"></canvas>
        </div>
        <div class="controls">
            <button @click="startWebRTC" :disabled="isWebrtcActive">Start Stream</button>
            <button @click="stopWebRTC" :disabled="!isWebrtcActive">Stop Stream</button>
        </div>
      </div>
      <div class="log-column">
        <div class="log-container">
          <h2>Log</h2>
          <textarea readonly :value="logMessages"></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue';

const logHistory = ref([]);
const detections = ref([]);
const canvasRef = ref(null);
const videoRef = ref(null);
const ws = ref(null);
const isWebrtcActive = ref(false);

const logMessages = computed(() => logHistory.value.join('\n'));

let ctx = null;
let peerConnection = null;

function connectWebSocket() {
  if (ws.value && ws.value.readyState === WebSocket.OPEN) {
      console.log("WebSocket already open.");
      return;
  }
  
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${wsProtocol}//${window.location.host}/ws`;
  
  console.log(`Attempting to connect WebSocket to: ${wsUrl}`);
  addLogMessage(`Attempting WebSocket connection to ${wsUrl}...`);
  ws.value = new WebSocket(wsUrl);

  ws.value.onopen = () => {
      console.log("WebSocket connected"); 
      addLogMessage("WebSocket connected. Ready to negotiate WebRTC."); 
  };
  ws.value.onmessage = handleWebSocketMessage;
  ws.value.onerror = (error) => {
      console.error("WebSocket error:", error);
      addLogMessage(`WebSocket error: ${error.message || 'Connection failed'}`);
      isWebrtcActive.value = false;
      clearCanvas();
  };
  ws.value.onclose = (event) => {
    console.log("WebSocket disconnected:", event.reason);
    addLogMessage(`WebSocket disconnected. Code: ${event.code}, Reason: ${event.reason || '-'}.`);
    isWebrtcActive.value = false;
    stopWebRTC();
  };
}

async function handleWebSocketMessage(event) {
     try {
      const message = JSON.parse(event.data);
      
      if (message.type === 'answer' && peerConnection) {
          console.log("Received SDP Answer:", message.sdp);
          addLogMessage("Received SDP Answer from backend.");
          try {
              await peerConnection.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: message.sdp }));
              addLogMessage("Remote description (answer) set.");
          } catch (e) {
              console.error("Error setting remote description:", e);
              addLogMessage(`Error setting remote description: ${e.message}`);
              stopWebRTC();
          }
      } else if (message.type === 'ice_candidate' && peerConnection && message.candidate) {
           try {
                console.log("Received ICE candidate:", message.candidate);
                await peerConnection.addIceCandidate(new RTCIceCandidate(message.candidate));
            } catch (e) {
                console.error("Error adding received ICE candidate:", e);
                addLogMessage(`Warn: Error adding ICE candidate: ${e.message}`);
            }
      } else if (message.type === 'detections') {
        detections.value = message.data;
        drawDetections();
      } else if (message.type === 'log') {
        addLogMessage(`${message.timestamp} - ${message.message}`, false);
      } else if (message.type === 'log_history') {
        const reversedHistory = message.data.reverse(); 
        logHistory.value = reversedHistory.map(item => `[BKND] ${item.timestamp} - ${item.message}`);
      }
    } catch (error) {
      console.error("Failed to parse WebSocket message:", error);
      addLogMessage(`Error processing message: ${error}`);
    }
}

function addLogMessage(message, isClient = true) {
  const prefix = isClient ? "[Client] " : "";
  logHistory.value.unshift(`${prefix}${message}`);
  if (logHistory.value.length > 150) { logHistory.value.pop(); }
}

async function startWebRTC() {
  if (peerConnection) { stopWebRTC(); }
  if (!ws.value || ws.value.readyState !== WebSocket.OPEN) {
      addLogMessage("WebSocket not connected. Please wait or check backend.");
      connectWebSocket();
      return;
  }

  isWebrtcActive.value = true;
  addLogMessage("Starting WebRTC negotiation...");

  peerConnection = new RTCPeerConnection();

  peerConnection.onicecandidate = (event) => {
      if (event.candidate && ws.value && ws.value.readyState === WebSocket.OPEN) {
          console.log("Sending ICE candidate to backend:", event.candidate);
          ws.value.send(JSON.stringify({ type: 'ice_candidate', candidate: event.candidate.toJSON() }));
      } 
  };

  peerConnection.ontrack = (event) => {
    if (videoRef.value && event.streams && event.streams[0]) {
        videoRef.value.srcObject = event.streams[0];
        addLogMessage("WebRTC video track received and attached.");
    } else {
         addLogMessage("Error: Received track but video element not ready.");
    }
  };

  peerConnection.onconnectionstatechange = () => {
      if (peerConnection) {
          const state = peerConnection.connectionState;
          addLogMessage(`WebRTC Connection State: ${state}`);
          console.log(`WebRTC Connection State: ${state}`);
          if (state === 'failed' || state === 'disconnected' || state === 'closed') {
               addLogMessage("WebRTC connection failed or closed.");
               stopWebRTC();
          }
      }
  };

  peerConnection.addTransceiver('video', { direction: 'recvonly' });

  try {
    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    if (ws.value && ws.value.readyState === WebSocket.OPEN) {
        addLogMessage("Sending SDP offer via WebSocket...");
        ws.value.send(JSON.stringify({ type: 'offer', sdp: offer.sdp }));
    } else {
        throw new Error("WebSocket not open when trying to send offer.");
    }
  } catch (error) {
    console.error('WebRTC negotiation failed:', error);
    addLogMessage(`WebRTC negotiation failed: ${error.message}`);
    stopWebRTC();
  }
}

function stopWebRTC() {
  if (peerConnection) {
    peerConnection.close();
    peerConnection = null;
  }
  if (videoRef.value) {
      videoRef.value.srcObject = null;
  }
  detections.value = [];
  clearCanvas();
  addLogMessage("WebRTC stream stopped.");
  isWebrtcActive.value = false;
}

function adjustCanvas() {
  nextTick(() => { 
    if (canvasRef.value && videoRef.value && videoRef.value.videoWidth > 0) {
      const video = videoRef.value;
      const canvas = canvasRef.value;
      
      canvas.width = video.offsetWidth;
      canvas.height = video.offsetHeight;
      canvas.style.position = 'absolute';
      const containerRect = video.parentElement.getBoundingClientRect();
      const videoRect = video.getBoundingClientRect();
      canvas.style.left = (videoRect.left - containerRect.left) + 'px';
      canvas.style.top = (videoRect.top - containerRect.top) + 'px';
      
      ctx = canvas.getContext('2d');
      console.log('Canvas adjusted:', canvas.width, canvas.height);
      drawDetections(); 
    }
  });
}

function drawDetections() {
    if (!ctx || !videoRef.value || videoRef.value.videoWidth === 0) return;

    clearCanvas();

    const scaleX = canvasRef.value.width / videoRef.value.videoWidth;
    const scaleY = canvasRef.value.height / videoRef.value.videoHeight;

    ctx.strokeStyle = 'lime';
    ctx.lineWidth = 2;
    ctx.font = '14px Arial';
    ctx.fillStyle = 'lime';

    detections.value.forEach(det => {
        const [x1, y1, x2, y2] = det.box;
        const dispX1 = x1 * scaleX;
        const dispY1 = y1 * scaleY;
        const dispWidth = (x2 - x1) * scaleX;
        const dispHeight = (y2 - y1) * scaleY;

        ctx.strokeRect(dispX1, dispY1, dispWidth, dispHeight);

        const label = `${det.label} (${det.confidence.toFixed(2)})`;
        const textWidth = ctx.measureText(label).width;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(dispX1, dispY1 - 18, textWidth + 4, 18); 
        ctx.fillStyle = 'lime';
        ctx.fillText(label, dispX1 + 2, dispY1 - 5);
    });
}

function clearCanvas() {
  if (ctx && canvasRef.value) {
    ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
  }
}

onMounted(() => {
  window.addEventListener('resize', adjustCanvas);
  if (videoRef.value) {
       videoRef.value.addEventListener('playing', adjustCanvas);
  } else {
       console.error("Video element ref not available on mount");
  }
  connectWebSocket();
});

onUnmounted(() => {
  stopWebRTC();
  if (ws.value && ws.value.readyState === WebSocket.OPEN) {
      ws.value.onclose = null;
      ws.value.close();
  }
  window.removeEventListener('resize', adjustCanvas);
  if (videoRef.value) {
       videoRef.value.removeEventListener('playing', adjustCanvas);
  }
});

</script>

<style scoped>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  color: #2c3e50;
}

h1 {
  text-align: center;
  width: 100%;
  margin-bottom: 20px;
}

.main-content {
  display: flex;
  flex-direction: row; 
  width: 95%; 
  margin: 0 auto; 
  gap: 20px; 
}

.video-column {
  flex: 1; 
  display: flex; 
  flex-direction: column;
  align-items: center; 
}

.log-column {
  flex: 2; 
  display: flex; 
  flex-direction: column;
}

.stream-container {
  position: relative; 
  width: 100%; 
  max-width: 100%; 
  margin-bottom: 0; 
}

#video-stream {
  display: block; 
  width: 100%; 
  max-width: 100%;
  height: auto;
  background-color: #333;
}

#overlay-canvas {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none; 
}

.log-container {
  width: 100%; 
  display: flex;
  flex-direction: column;
  height: 100%; 
}

.log-container h2 {
  text-align: center;
  flex-shrink: 0; 
}

.log-container textarea {
  width: 100%;
  flex-grow: 1; 
  font-family: monospace;
  font-size: 0.9em;
  border: 1px solid #ccc;
  box-sizing: border-box; 
  min-height: 300px; 
}

.controls {
    margin-top: 10px;
    display: flex;
    gap: 10px;
}
</style>
